import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { isEqual } from 'lodash';
import { TALKIATRY_PHONE_NUMBER_DISPLAY } from '../../../helpers/const';
import { selectMatching } from '../../../redux/matching';
import { AssessmentCard, AssessmentHeader } from '../shared';
import { Icon, IconTypes } from '../../icon/icon';
import { formatProviderNameWithCredential } from '../../../helpers/format-helpers';
import { selectReferralSource } from '../../../redux/session';
import { ReferralSource } from '../../../redux/types';

type AppointmentListItemProps = {
  icon: IconTypes;
  description: string;
};

const AppointmentListItem = ({
  icon,
  description,
}: AppointmentListItemProps) => (
  <ListItem disableGutters disablePadding>
    <ListItemIcon sx={{ minWidth: 40 }}>
      <Icon icon={icon} />
    </ListItemIcon>
    <ListItemText primary={description} />
  </ListItem>
);

const AppointmentConfirmation = () => {
  const { provider, zocdocProvider, appointmentTime } =
    useSelector(selectMatching);
  const referralSource = useSelector(selectReferralSource);
  const isZocdocReferral = referralSource === ReferralSource.Zocdoc;

  const getHeader = () => {
    if (!isZocdocReferral) return 'Your appointment is booked!';
    if (isEqual(provider, zocdocProvider))
      return 'Thank you for completing the assessment. Your visit is booked.';
    return 'Your new appointment has been booked';
  };
  const header = getHeader();

  return (
    <>
      <AssessmentHeader
        icon={
          <Icon icon="CircleCheck" style={{ fontSize: 48 }} color="success" />
        }
        title={header}
        subtitle="Thank you for trusting us with your care! We look forward to meeting you and supporting you on your mental health care journey."
      />
      <AssessmentCard>
        <List disablePadding>
          <AppointmentListItem
            icon="CalendarCheck"
            description={format(new Date(appointmentTime), 'EEEE, LLLL d, Y')}
          />
          <AppointmentListItem
            icon="Clock"
            description={`${format(new Date(appointmentTime), 'p')} ${new Date(
              appointmentTime
            )
              .toString()
              .split('(')[1]
              .replace(/\)/, '')}`}
          />
          <AppointmentListItem
            icon="Stethoscope"
            description={formatProviderNameWithCredential(
              provider?.firstName,
              provider?.lastName,
              provider?.primaryCredential
            )}
          />
          <AppointmentListItem
            icon="Video"
            description="Televisit Appointment"
          />
        </List>
      </AssessmentCard>
      <AssessmentCard>
        <Alert severity="info">
          <AlertTitle>Important</AlertTitle>
          <Typography paragraph>
            You must finish all the steps below before your first appointment.
          </Typography>
          <AlertTitle>For Appointment Changes:</AlertTitle>
          <Typography>
            Please call us at {TALKIATRY_PHONE_NUMBER_DISPLAY} if you need to
            cancel or reschedule your appointment.
          </Typography>
        </Alert>
        <List>
          <ListItem disableGutters>
            <ListItemText
              primary="There are a few simple steps to take before seeing your Talkiatry
          psychiatrist for the first time."
            />
          </ListItem>
          <ListItem disableGutters alignItems="flex-start">
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Icon icon="Circle1" />
            </ListItemIcon>
            <ListItemText
              primary="Upload required documents and files"
              primaryTypographyProps={{
                fontWeight: 500,
                mt: 0.2,
              }}
              secondary={
                <>
                  <Typography variant="body2" fontSize="1rem" sx={{ mt: 1 }}>
                    Upload the front and back of your insurance card. Upload the
                    front and back of your driver's license or official state
                    ID.
                  </Typography>
                  <Button
                    variant="outlined"
                    sx={{ mt: 2 }}
                    href="/document-upload"
                    target="_blank"
                    endIcon={
                      <Icon
                        icon="ArrowUpRightFromSquare"
                        sx={{ mr: 0.5 }}
                        style={{ fontSize: 12 }}
                      />
                    }
                  >
                    Upload documents
                  </Button>
                </>
              }
            />
          </ListItem>
          <ListItem disableGutters alignItems="flex-start">
            <ListItemIcon sx={{ minWidth: 40 }}>
              <Icon icon="Circle2" />
            </ListItemIcon>
            <ListItemText
              primary="Look for our confirmation email"
              primaryTypographyProps={{
                fontWeight: 500,
                mt: 0.2,
              }}
              secondary={
                <Box>
                  <Typography variant="body2" fontSize="1rem" sx={{ mt: 1 }}>
                    You will receive a confirmation email in the next 24-48
                    hours. You will receive your username, password, and
                    instructions on how to sign in to the Patient Portal.
                  </Typography>
                </Box>
              }
            />
          </ListItem>
        </List>
        <Typography>
          You may receive a confirmation call from our care coordination team
          which might appear as{' '}
          <Box component="span" fontWeight="600">
            Talkiatry Management Services
          </Box>{' '}
          on your caller ID.
        </Typography>
        <Button
          fullWidth
          color="primary"
          size="large"
          variant="contained"
          href="https://www.talkiatry.com/"
          sx={{ mt: 3 }}
        >
          Done
        </Button>
        <Button
          fullWidth
          variant="outlined"
          size="large"
          href="mailto:hello@talkiatry.com"
          sx={{ mt: 2 }}
        >
          Contact our support team
        </Button>
      </AssessmentCard>
    </>
  );
};

export default AppointmentConfirmation;
